import { useCallback, useEffect, useState } from 'react';
import { ApiClientError } from '../lib/api';

/**
 * Fetch-once-and-reload hook shared by every screen.
 *
 * Deliberately small: this app's data is a handful of lists behind a session,
 * so a query library would be more machinery than the problem needs. If caching
 * across screens or optimistic updates become real requirements, replace this
 * one hook rather than each screen.
 */
export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
}

export function useResource<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
  options: { refreshMs?: number } = {}
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError(null);

    try {
      setData(await fetcher());
    } catch (caught) {
      // Background refreshes fail silently — the last good data stays up.
      if (!background) {
        setError(
          caught instanceof ApiClientError
            ? caught.message
            : 'Something went wrong loading this data'
        );
      }
    } finally {
      if (!background) setLoading(false);
    }
    // The caller owns the dependency list — `fetcher` is usually an inline
    // closure and would otherwise re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void load();

    // Optional background polling: credits arrive via server-side
    // reconciliation, so balances update without user action.
    if (options.refreshMs) {
      const timer = setInterval(() => void load(true), options.refreshMs);
      return () => clearInterval(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, options.refreshMs]);

  return { data, error, loading, reload: () => load(false) };
}
